/**
 * Composable for managing audio chapter navigation
 */

import { ref, computed, watch, type Ref, type ComputedRef } from 'vue'
import type { AudioChapter } from '../types'

export interface UseAudioChaptersOptions {
  chapters: Ref<AudioChapter[]>
  currentTime: Ref<number>
  duration: Ref<number>
  onChapterChange?: (index: number, chapter: AudioChapter) => void
}

export interface UseAudioChaptersReturn {
  currentChapterIndex: Ref<number>
  currentChapter: ComputedRef<AudioChapter | undefined>
  hasChapters: ComputedRef<boolean>
  getChapterAtTime: (time: number) => AudioChapter | undefined
  goToChapter: (index: number) => number
}

export function useAudioChapters(options: UseAudioChaptersOptions): UseAudioChaptersReturn {
  const { chapters, currentTime, onChapterChange } = options

  const currentChapterIndex = ref(-1)

  const hasChapters = computed(() => chapters.value.length > 0)

  const currentChapter = computed(() => {
    if (currentChapterIndex.value < 0) return undefined
    return chapters.value[currentChapterIndex.value]
  })

  function getChapterAtTime(time: number): AudioChapter | undefined {
    const chaps = chapters.value
    if (chaps.length === 0) return undefined

    for (let i = chaps.length - 1; i >= 0; i--) {
      if (time >= chaps[i].startTime) {
        return chaps[i]
      }
    }
    return undefined
  }

  function findChapterIndex(time: number): number {
    const chaps = chapters.value
    if (chaps.length === 0) return -1

    for (let i = chaps.length - 1; i >= 0; i--) {
      if (time >= chaps[i].startTime) {
        return i
      }
    }
    return -1
  }

  /**
   * Returns the start time of the chapter at the given index
   */
  function goToChapter(index: number): number {
    const chaps = chapters.value
    if (index < 0 || index >= chaps.length) return 0
    return chaps[index].startTime
  }

  // Watch currentTime to update chapter index
  watch(currentTime, (time) => {
    if (!hasChapters.value) return

    const newIndex = findChapterIndex(time)
    if (newIndex !== currentChapterIndex.value) {
      currentChapterIndex.value = newIndex
      if (newIndex >= 0) {
        onChapterChange?.(newIndex, chapters.value[newIndex])
      }
    }
  })

  // Reset when chapters change
  watch(chapters, () => {
    currentChapterIndex.value = findChapterIndex(currentTime.value)
  }, { immediate: true })

  return {
    currentChapterIndex,
    currentChapter,
    hasChapters,
    getChapterAtTime,
    goToChapter,
  }
}
